/**
 * Ficha de um cliente (`/clientes/:id`): dados cadastrais (editáveis pelo mesmo
 * modal usado em `pages/Clientes.tsx`) e as propostas recentes.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fmtDate, labelDocumento, PROPOSAL_STATUS_LABEL } from '@/lib/format';
import type { ProposalStatus } from '@/lib/types';
import { useCliente, type PropostaRecenteCliente } from '@/hooks/useClientes';
import { PageHeader } from '@/components/PageHeader';
import { Panel } from '@/components/Panel';
import { Button } from '@/components/Button';
import { Badge, type BadgeVariant } from '@/components/Badge';
import { Money } from '@/components/Money';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner';
import { ModalCliente } from './Clientes';
import './Clientes.css';

const STATUS_TOM: Record<ProposalStatus, BadgeVariant> = {
  rascunho: 'neutral',
  emitida: 'neutral',
  aceita: 'positive',
  recusada: 'alert',
  cancelada: 'alert',
};

export function ClienteDetalhe() {
  const { id } = useParams();
  const clienteId = Number(id);
  const clienteQuery = useCliente(Number.isFinite(clienteId) ? clienteId : undefined);
  const [editando, setEditando] = useState(false);

  if (clienteQuery.isLoading) {
    return (
      <div className="cli-loading">
        <Spinner label="Carregando cliente…" />
      </div>
    );
  }

  if (!clienteQuery.data) {
    return (
      <EmptyState
        title="Cliente não encontrado"
        description="Ele pode ter sido excluído do cadastro."
        action={
          <Link to="/clientes">
            <Button>Voltar a clientes</Button>
          </Link>
        }
      />
    );
  }

  const cliente = clienteQuery.data;

  const colunas: DataTableColumn<PropostaRecenteCliente>[] = [
    {
      key: 'code',
      header: 'Código',
      render: (row) => (
        <Link to={`/propostas/${row.id}`} className="mono">
          {row.code}
        </Link>
      ),
    },
    { key: 'data', header: 'Data', render: (row) => <span className="mono">{fmtDate(row.createdAt.slice(0, 10))}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge variant={STATUS_TOM[row.status]}>{PROPOSAL_STATUS_LABEL[row.status] ?? row.status}</Badge>,
    },
    { key: 'total', header: 'Total geral', numeric: true, render: (row) => <Money value={row.totalGeral} /> },
  ];

  return (
    <>
      <PageHeader
        title={cliente.razaoSocial}
        meta={
          <Link to="/clientes" className="muted">
            Voltar a clientes
          </Link>
        }
        actions={<Badge variant={cliente.active ? 'positive' : 'neutral'}>{cliente.active ? 'Ativo' : 'Inativo'}</Badge>}
      />

      <div className="stack-lg">
        <Panel
          title="Dados do cliente"
          actions={
            <Button size="sm" onClick={() => setEditando(true)}>
              Editar
            </Button>
          }
        >
          <dl className="cli-detalhe-grid">
            <div>
              <dt>{labelDocumento(cliente.cnpj)}</dt>
              <dd className="mono">{cliente.cnpj}</dd>
            </div>
            <div>
              <dt>UF</dt>
              <dd>{cliente.uf || '—'}</dd>
            </div>
            <div>
              <dt>Contato</dt>
              <dd>{cliente.contato || '—'}</dd>
            </div>
            <div>
              <dt>Celular</dt>
              <dd className="mono">{cliente.celular || '—'}</dd>
            </div>
            <div>
              <dt>E-mail</dt>
              <dd>{cliente.email || '—'}</dd>
            </div>
            <div>
              <dt>Propostas</dt>
              <dd>{cliente.proposalsCount}</dd>
            </div>
          </dl>
          {cliente.notes ? <p className="muted cli-detalhe-notes">{cliente.notes}</p> : null}
        </Panel>

        <Panel title="Propostas recentes">
          <DataTable
            columns={colunas}
            rows={cliente.recentProposals}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Nenhuma proposta ainda"
                description="Quando este cliente entrar em uma proposta, ela aparece aqui."
              />
            }
          />
        </Panel>
      </div>

      {editando ? <ModalCliente modo="editar" cliente={cliente} onClose={() => setEditando(false)} /> : null}
    </>
  );
}
